/**
 * Operator impersonation ("open workspace as") for Triveny platform admins.
 *
 * A platform operator (Triveny.ai) can open a customer tenant (e.g. Kloudfuse)
 * as its admin for support. We swap the stored auth token for an impersonation
 * token minted by POST /api/platform/auth/organizations/{id}/impersonate, after
 * backing up the operator's own session so we can restore it on exit.
 *
 * SECURITY: we drop active_cbo_id when entering impersonation. The impersonation
 * JWT carries its own cbo_id (or none), and the backend refuses to honor an
 * X-CBO-ID header for impersonation tokens — but clearing it here is defense in
 * depth so a stale operator cbo_id (e.g. Triveny.ai 609) can never be sent.
 */

const IMPERSONATION_KEY = 'triveny_impersonation';
const OPERATOR_BACKUP_KEY = 'triveny_operator_backup';

export interface ImpersonationState {
  tenantId: string;
  name: string;
  slug: string;
  impersonator: string; // operator email
}

export function getImpersonation(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationState) : null;
  } catch {
    return null;
  }
}

export function isImpersonating(): boolean {
  return getImpersonation() !== null;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

/**
 * Open the given tenant as its admin. On success this reloads the app into the
 * impersonated workspace. Throws on failure (caller surfaces the message).
 */
export async function startImpersonation(
  base: string,
  org: { id: string; name: string; slug: string },
): Promise<void> {
  const res = await fetch(`${base}/api/platform/auth/organizations/${org.id}/impersonate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.detail || `Couldn't open ${org.name} (${res.status})`);
  }

  // Back up the operator's own session BEFORE we overwrite it.
  const backup = {
    auth_token: localStorage.getItem('auth_token'),
    refresh_token: localStorage.getItem('refresh_token'),
    token_expires_at: localStorage.getItem('token_expires_at'),
    active_cbo_id: localStorage.getItem('active_cbo_id'),
    platform_tenant_slug: localStorage.getItem('platform_tenant_slug'),
  };
  localStorage.setItem(OPERATOR_BACKUP_KEY, JSON.stringify(backup));

  // Swap in the impersonation token. No refresh (deliberately non-renewable).
  localStorage.setItem('auth_token', body.access_token);
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_expires_at');
  localStorage.removeItem('active_cbo_id'); // never send a stale operator cbo_id
  if (body?.tenant?.slug) localStorage.setItem('platform_tenant_slug', body.tenant.slug);

  localStorage.setItem(
    IMPERSONATION_KEY,
    JSON.stringify({
      tenantId: org.id,
      name: body?.tenant?.name || org.name,
      slug: body?.tenant?.slug || org.slug,
      impersonator: body?.user?.impersonator_email || '',
    } satisfies ImpersonationState),
  );
  localStorage.removeItem('activeTab');
  window.location.href = window.location.origin;
}

/** Restore the operator's own session and reload back to Triveny.ai. */
export function exitImpersonation(): void {
  try {
    const raw = localStorage.getItem(OPERATOR_BACKUP_KEY);
    if (raw) {
      const b = JSON.parse(raw) as Record<string, string | null>;
      const restore = (k: string, v: string | null) => {
        if (v == null) localStorage.removeItem(k);
        else localStorage.setItem(k, v);
      };
      restore('auth_token', b.auth_token);
      restore('refresh_token', b.refresh_token);
      restore('token_expires_at', b.token_expires_at);
      restore('active_cbo_id', b.active_cbo_id);
      restore('platform_tenant_slug', b.platform_tenant_slug);
    }
  } catch {
    /* fall through to clearing flags */
  }
  localStorage.removeItem(IMPERSONATION_KEY);
  localStorage.removeItem(OPERATOR_BACKUP_KEY);
  localStorage.removeItem('activeTab');
  window.location.href = window.location.origin;
}
